import { useDisclosure } from '@chakra-ui/hooks'
import { Box, Flex } from '@chakra-ui/layout'
import { Skeleton, Text, useMediaQuery, Wrap, WrapItem } from '@chakra-ui/react'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import * as API from '../api'
import {
  AddCard,
  ChangeLanguage,
  type ChangeLanguageHandler,
  HtmlContainer,
  ImageCard,
  PoiInfo,
  Title,
  UploadFiles,
} from '../components'
import { graphicalCharterLink } from '../i18n'
import { useUserAwareTranslation } from '../i18n/hooks'

//Si bandeauMessage est à true pesnsez à renseigner plus bas le contenu de ce message
const activateMessageBanner = false

interface MainProps {
  user: API.APIUser
  enableLanguageChange: boolean
  changeLanguageHandler: ChangeLanguageHandler
}

const Main = ({ user, enableLanguageChange, changeLanguageHandler }: MainProps) => {
  const { t } = useUserAwareTranslation(user)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([] as API.APIAsset[])
  //const [selectedAsset, setSelectedAsset] = useState(undefined)
  const [isMobile] = useMediaQuery('(max-width: 479px)')
  const [isWithSmallHeight] = useMediaQuery('(max-height: 730px)')
  const [graphicalCharterPage, setGraphicalCharterPage] = useState('')

  const getData = async () => {
    const inspecId = _.get(user, 'inspecId')
    if (inspecId) {
      const getAssetsResult = await API.getAssets(user.token, inspecId)
      if (!_.isEmpty(getAssetsResult.assets)) {
        const assets = _.get(getAssetsResult, 'assets', []).filter(
          (e: API.APIAsset) => !_.isEmpty(e)
        )
        if (!_.isEmpty(assets)) {
          let change = data.length != assets.length
          let changeCount = 0
          if (!change) {
            const dataIds = data.map((d) => d.id)
            changeCount = assets.filter((a) => !dataIds.includes(a.id)).length
            change = changeCount > 0
          }
          if (change) {
            console.debug('New assets count ', changeCount)
            setData(assets)
          }
        } else {
          setData([])
        }
      }
      // if (err) {
      //   console.log('[Main.getData]', err)
      // }
    }
    setLoading(false)
  }

  const callGetData = () => {
    console.log('callGetData')
    return getData()
  }

  useEffect(() => {
    console.log('useEffect')
    getData()
    graphicalCharterLink(user).then((pdf) => setGraphicalCharterPage(pdf || ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {activateMessageBanner ? (
        <Box
          w="100%"
          bg="transparent"
          p={2}
          boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)"
          display="flex"
          justifyContent="center">
          <Box
            bg="#BA0B2F"
            color={'white'}
            borderRadius="lg"
            p={6}
            textAlign="center"
            maxWidth={{ base: '100%', lg: '100%' }}
            w="100%"
            display="flex"
            flexDirection={{ base: 'column', lg: 'column' }}
            alignItems="center"
            gap={6}>
            <Text width="100%" textAlign="center">
              {' '}
              📢 Maintenance is scheduled from <strong>8.30 am on Friday 29 May</strong> until{' '}
              <strong>10.00 am on Monday 1 June</strong>. Our Uploader app for sending your photos
              will be unavailable during this time. Please note that the app may become available
              again earlier than this.
            </Text>
            <Text width="100%" textAlign="center">
              📢Une maintenance est prévue du <strong>vendredi 29 mai à 8h30</strong> au{' '}
              <strong>
                lundi 1<sup>er</sup> juin à 10h
              </strong>
              . Notre application Uploader pour l'envoi de vos photos ne sera pas disponible pendant
              cette période. Veuillez noter que l'application pourrait être à nouveau disponible
              plus tôt que prévu.
            </Text>
          </Box>
        </Box>
      ) : (
        <></>
      )}
      <Flex
        px={[6, 20, 20, 24, 40]}
        pb={enableLanguageChange ? '0px' : '6'}
        bg="#F5F3F1"
        flexDirection="column"
        gap={{ base: '4', md: '0' }}>
        <PoiInfo user={user} />
        <ChangeLanguage
          isActive={enableLanguageChange}
          languages={[
            {
              label: t('language1'),
              code: t('language1-code'),
            },
            {
              label: t('language2'),
              code: t('language2-code'),
            },
          ]}
          user={user}
          changeLanguageHandler={changeLanguageHandler}
        />
      </Flex>
      <Box pt={10} pb={10} px={[6, 20, 20, 24, 40]}>
        <UploadFiles
          //data={selectedAsset}
          user={user}
          isOpen={isOpen}
          onClose={onClose}
          reloadHandler={callGetData}
        />
        <Box opacity={isOpen ? 0.1 : 1} pointerEvents={isOpen ? 'none' : 'unset'}>
          <Title text={t('images-title')} />

          <Box color="gray.500" fontSize={'14px'} mb={10}>
            <HtmlContainer html={t('images-subtitle', { guidelines: graphicalCharterPage })} />
          </Box>
          <Wrap>
            <WrapItem w={['100%', '270px']}>
              <AddCard
                user={user}
                onClick={() => {
                  onOpen()
                  window.scrollTo(0, isMobile ? 150 : isWithSmallHeight ? 150 : 0)
                }}
              />
            </WrapItem>
            {loading
              ? [...Array(9).keys()].map((e) => (
                  <WrapItem key={e} w={['100%', '270px']}>
                    <Skeleton
                      startColor="gray.100"
                      endColor="gray.200"
                      w="100%"
                      h={['147px', '340px']}
                      borderRadius={4}
                      boxShadow="lg"
                      m={[0, 4]}
                      ml={[0]}
                    />
                  </WrapItem>
                ))
              : data.map((e) => (
                  <WrapItem key={e.id} w={['100%', '270px']}>
                    <ImageCard
                      user={user}
                      id={e.id}
                      title={e.title}
                      image={e.image}
                      subject={t(e.subjectIdentifier)}
                      copyright={e.copyright}
                      otherProvider={e.otherProvider}
                      poiName={_.get(user, 'poiName')}
                      withActions={true}
                      /*editHandler={() => {
                        setSelectedAsset({
                          assetId: e.id,
                          title: e.title,
                          subject: e.subjectIdentifier,
                          quality: e.qualityIdentifier,
                          relateddInspecId: e.relateddInspecId,
                          author: e.author,
                          otherProvider: e.otherProvider,
                          uploaderEmail: e.uploaderEmail,
                          uploaderName: e.uploaderName,
                          uploaderPosition: e.uploaderPosition,
                        })
                        onOpen()
                        window.scrollTo(0, isMobile ? 150 : 200)
                      }}*/
                      reloadHandler={callGetData}
                    />
                  </WrapItem>
                ))}
          </Wrap>
        </Box>
      </Box>
    </>
  )
}

export default Main
